/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { callApi } from 'api/helpers/api.helpers';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import addressBookApi from '../../api/addressBook.api';
import { ImportStatus } from './importStatus';

export default function useImportContactDialog(onCancel, loadData) {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [successfulImport, setSuccessfulImport] = useState(false);

  useEffect(() => {
    async function fetchImports() {
      const importResponse = await callApi(addressBookApi.getImportByStatusAndUser)(ImportStatus.MERGING);
      // !!!! Fix this?? Can we assume this will only have one element?
      setImportResult(importResponse?.content?.[0]);
      setSuccessfulImport(importResponse?.content?.status === ImportStatus.RESOLVED);
    }
    fetchImports();

    return () => setImportResult(null);
  }, []);

  const onChange = ([loadedFile]) => {
    setErrorMessage(null);
    setSuccessfulImport(false);
    setFile(loadedFile);
  };

  const onClose = () => {
    onCancel();
    setFile(null);
    setErrorMessage(null);
    setImportResult(null);
    setSuccessfulImport(false);
    loadData();
  };

  const onDropRejected = (rejectedFiles) => {
    if (rejectedFiles.length > 1) {
      setErrorMessage(t('messages.not.allowed.more.one.files'));
    } else if (!rejectedFiles[0].name?.endsWith('.csv')) {
      setErrorMessage(t('messages.only.csv'));
    } else {
      setErrorMessage(t('messages.file.upload.error'));
    }
  };

  const onSend = async () => {
    if (file) {
      const importResponse = await callApi(addressBookApi.createImport)(file);
      setImportResult(importResponse);
      setSuccessfulImport(importResponse.status === ImportStatus.RESOLVED);
    }
  };

  const onClear = () => setFile(null);

  const onAllDuplicatesResolved = async (resolvedList) => {
    await callApi(addressBookApi.resolveImportDuplicates)(importResult.id, resolvedList);
    setSuccessfulImport(true);
  };

  const onCancelImport = async () => {
    if (importResult.status === ImportStatus.MERGING) {
      const { id, payload } = importResult;
      await callApi(addressBookApi.updateImport)(id, {
        ...payload,
        status: ImportStatus.CANCELLED,
      });
    }
    onClose();
  };

  return {
    file,
    errorMessage,
    importResult,
    successfulImport,
    setSuccessfulImport,
    onChange,
    onClose,
    onDropRejected,
    onSend,
    onClear,
    onAllDuplicatesResolved,
    onCancelImport,
    t,
  };
}
